import type { ConversationId, UserId } from '@cyphex/types';
import { PRESENCE_COALESCE_MS, TYPING_TTL_MS } from '../constants';
import type { TypingEntry } from '../types';
import { createStore } from './createStore';

interface TypingState {
  map: ReadonlyMap<string, readonly TypingEntry[]>;
  version: number;
}

const store = createStore<TypingState>({ map: new Map(), version: 0 });
const EMPTY: readonly TypingEntry[] = [];

let pending: Map<string, TypingEntry[]> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sweepTimer: ReturnType<typeof setInterval> | null = null;

function publish(next: Map<string, TypingEntry[]>): void {
  store.set((previous) => ({ map: next, version: previous.version + 1 }));
}

/** Coalesces bursts of typing frames into one React update per window. */
function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (pending) publish(pending);
    pending = null;
  }, PRESENCE_COALESCE_MS);
}

function draft(): Map<string, TypingEntry[]> {
  if (!pending) {
    pending = new Map<string, TypingEntry[]>();
    store.get().map.forEach((value, key) => pending?.set(key, [...value]));
  }
  return pending;
}

function sweep(): void {
  const now = Date.now();
  const next = new Map<string, TypingEntry[]>();
  let changed = false;
  store.get().map.forEach((entries, key) => {
    const live = entries.filter((entry) => entry.expiresAt > now);
    if (live.length !== entries.length) changed = true;
    if (live.length > 0) next.set(key, live);
  });
  if (changed) publish(next);
}

export const typingStore = {
  subscribe: store.subscribe,

  select(conversationId: ConversationId): readonly TypingEntry[] {
    return store.get().map.get(conversationId) ?? EMPTY;
  },

  /** Applied from a `typing` socket frame. Never triggers a render directly. */
  mark(conversationId: ConversationId, userId: UserId, displayName: string, expiresAt?: number): void {
    const next = draft();
    const entries = (next.get(conversationId) ?? []).filter((entry) => entry.userId !== userId);
    entries.push({ userId, displayName, expiresAt: expiresAt ?? Date.now() + TYPING_TTL_MS });
    next.set(conversationId, entries);
    scheduleFlush();
  },

  clear(conversationId: ConversationId, userId: UserId): void {
    const next = draft();
    const entries = (next.get(conversationId) ?? []).filter((entry) => entry.userId !== userId);
    if (entries.length > 0) next.set(conversationId, entries);
    else next.delete(conversationId);
    scheduleFlush();
  },

  /** Started once by the chat shell; expires stale indicators without polling the server. */
  startSweeper(): () => void {
    if (!sweepTimer) sweepTimer = setInterval(sweep, TYPING_TTL_MS / 2);
    return () => {
      if (sweepTimer) clearInterval(sweepTimer);
      sweepTimer = null;
    };
  },
};
